import React, { useEffect, useState } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { AddButton, CancelButton, SubmitButton, UpdateButton } from '../../../components/mui/Buttons/Buttons';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import useResponsive from '../../../Hooks/useResponsive';
import {
  clearQuotationsDataArray,
  commonFileUpload,
  createRequirement,
  getAllProductsWithoutPagination,
  getAllVendorsWithoutPagination,
  getQuotationRequirements,
  getQuotationsOfRequrements,
  updateQuotations,
} from '../../../redux/features/assetManagementSlice';
import Loader3D from '../../../components/Loader/Loader3D';
import { savePaginationData } from '../../../redux/features/globalDatagridSlice';
import AddProduct from '../product/AddProduct';
import { debounce } from 'lodash';
import { GoalFileUpload } from '../../../redux/features/appraisalSlice';
import { uploadedFileUrl } from '../../../utils/constants/links';

const paymentTypes = [
  { id: 1, name: 'Non-Installment' },
  { id: 2, name: 'Installment' },
];

const AddOrUpdateQuotation = ({ row, dispatchParams, isEditable }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [direction, setDirection] = useState({});
  const mdDown = useResponsive('down', 'md');
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const {
    vendorData,
    productData,
    quotationsData,
    loading: mainLoading,
    fileUploadLoading,
  } = useSelector((store) => store.assetmanagement);
  const loading = mainLoading || fileUploadLoading;
  useEffect(() => {
    if (mdDown) {
      setDirection('column');
    } else {
      setDirection('row');
    }
  }, [mdDown]);

  useEffect(() => {
    if (open) {
      dispatch(getAllProductsWithoutPagination());
      dispatch(getAllVendorsWithoutPagination());
      if (row) {
        dispatch(getQuotationsOfRequrements(row.requirementID));
      }
    }
  }, [open, row]);

  useEffect(() => {
    if (row && open) {
      const existingQuotation = updateQuotationInitialStructure();
      setQuotations(existingQuotation);
      formik.setFieldValue('quotations', existingQuotation);
    } else if (open && !row) {
      const initialQuotation = [createInitialQuotation()];
      setQuotations(initialQuotation);
      formik.setFieldValue('quotations', initialQuotation);
    }
  }, [quotationsData, row, open]);

  function createInitialQuotation() {
    return {
      vendorId: '',
      cost: '',
      quantity: '',
      gst: '',
      discountAmount: '',
      courrierCharge: '',
      manualOverride: false,
      totalCost: '',
      deliveryDate: dayjs().format('YYYY-MM-DD'),
      paymentType: 1,
      installmentCount: null,
      installmentAmounts: [],
      isRecommended: false,
      quotationAttachedBackendFileName: '',
    };
  }

  function updateQuotationInitialStructure() {
    if (quotationsData.length > 0) {
      return quotationsData.map((data) => ({
        vendorId: data.vendorID,
        cost: data.cost,
        quantity: data.quantity,
        gst: data.gst,
        discountAmount: data.discountAmount,
        courrierCharge: data.courierCharge,
        manualOverride: false,
        totalCost: data.totalCost,
        deliveryDate: dayjs(data.deliveryDate).format('YYYY-MM-DD'),
        paymentType: data.paymentType,
        installmentCount: data.installmentCount,
        installmentAmounts: data.quotation_installments.map((installment) => parseFloat(installment?.amount)),
        isRecommended: data.isRecommended,
        quotationID: data.quotationID,
        quotationAttachedBackendFileName: data.attachmentFileName || '',
      }));
    } else {
      return [
        {
          vendorId: '',
          cost: '',
          quantity: '',
          gst: '',
          discountAmount: '',
          courrierCharge: '',
          totalCost: '',
          deliveryDate: dayjs().format('YYYY-MM-DD'),
          paymentType: 1,
          installmentCount: null,
          installmentAmounts: [],
          isRecommended: false,
          quotationAttachedBackendFileName: '',
        },
      ];
    }
  }
  const schema = Yup.object().shape({
    productId: Yup.number().required('Product is required'),
    quotations: Yup.array().of(
      Yup.object().shape({
        vendorId: Yup.number().required('Vendor is required'),
        cost: Yup.number().required('Cost is required'),
        quantity: Yup.number().required('Quantity is required'),
        courrierCharge: Yup.number().required('Courier charge is required'),
        totalCost: Yup.number().required('Total cost is required').moreThan(0, 'Total cost must be greater than zero'),
        deliveryDate: Yup.date().required('Delivery date is required'),
        paymentType: Yup.number().required('Payment type is required'),
        installmentCount: Yup.number().when('paymentType', {
          is: 2,
          then: Yup.number().min(1, 'At least one installment is required').required('Installment count is required'),
          otherwise: Yup.number().nullable(),
        }),
        // quotationAttachedBackendFileName: Yup.string().required('Quotation attachment is required'),
        quotationAttachedBackendFileName: Yup.string().when(['quantity', 'totalCost', 'cost'], {
          is: (quantity, totalCost, cost) => (quantity >= 2 && totalCost >= 15000) || (quantity ==1 && cost >= 10000),
          then: Yup.string().required('Quotation attachment is required'),
          otherwise: Yup.string().nullable(),
        }),
        // Validate installmentAmounts if paymentType is installment
        // installmentAmounts: Yup.array().when("paymentType", {
        //   is: 1,
        //   then: (schema) =>
        //     schema
        //       .of(Yup.number().required("Installment amount is required"))
        //       .min(1, "At least one installment amount is required"),
        //   otherwise: (schema) => schema.of(Yup.number().nullable()),
        // }),
      })
    ),
  });

  const formik = useFormik({
    initialValues: { productId: row ? row?.product_id : '', quotations },
    validationSchema: schema,
    validateOnChange: true,
    onSubmit: (values) => {
      const data = renameFormikValues(values);
      if (row) {
        const updateData = { ...data, id: row.requirementID };
        dispatch(updateQuotations(updateData)).then((res) => {
          if (res.payload.status === 'error') {
            enqueueSnackbar(res.payload.message, {
              variant: 'error',
            });
          } else if (res.payload.status === 'success') {
            handleCancel();
            dispatch(getQuotationRequirements(dispatchParams));
            dispatch(savePaginationData({ ...paginationData, openAnchor: false }));
            enqueueSnackbar(res.payload.message, {
              variant: 'success',
            });
          }
        });
      } else {
        dispatch(createRequirement(data)).then((res) => {
          if (res.payload.status === 'error') {
            enqueueSnackbar(res.payload.message, {
              variant: 'error',
            });
          } else if (res.payload.status === 'success') {
            handleCancel();
            dispatch(getQuotationRequirements(dispatchParams));
            enqueueSnackbar(res.payload.message, {
              variant: 'success',
            });
          }
        });
      }
    },
  });
  const handleProductChange = (event, option) => {
    const fieldName = event.target?.id.split('-')[0];
    formik.setFieldValue(fieldName, option?.product_id);
  };

  const handleCancel = () => {
    setOpen(false);
    formik.resetForm();
    dispatch(clearQuotationsDataArray());
  };

  const handleAddQuotation = () => {
    formik.setFieldValue('quotations', [...formik.values.quotations, createInitialQuotation()]);
  };

  const removeQuotation = () => {
    const updatedQuotations = [...formik.values.quotations];
    if (updatedQuotations.length > 1) {
      updatedQuotations.pop();
      formik.setFieldValue('quotations', updatedQuotations);
    }
  };

  const renameFormikValues = (data) => {
    return {
      productID: data.productId,
      quotation: data.quotations.map((q) => ({
        vendorID: q.vendorId,
        cost: q.cost,
        quantity: q.quantity,
        gst: q.gst,
        discountAmount: q.discountAmount,
        courierCharge: q.courrierCharge,
        totalCost: q.totalCost,
        deliveryDate: q.deliveryDate,
        paymentType: q.paymentType,
        installmentCount: q.paymentType == 2 ? q.installmentCount : 0,
        quotation_installments: q.paymentType == 2 ? q.installmentAmounts : [],
        isRecommended: q.isRecommended,
        quotationID: q?.quotationID ?? null,
        quotationAttachedBackendFileName: q.quotationAttachedBackendFileName,
      })),
    };
  };

  useEffect(() => {
    const debouncedCalculation = debounce(() => {
      formik.values.quotations.forEach((quotation, index) => {
        const cost = parseFloat(quotation.cost) || 0;
        const quantity = parseFloat(quotation.quantity) || 0;
        const gst = parseFloat(quotation.gst) || 0;
        const discountAmount = parseFloat(quotation.discountAmount) || 0;
        const courrierCharge = parseFloat(quotation.courrierCharge) || 0;
        const totalCost = parseFloat((cost * quantity + gst - discountAmount + courrierCharge).toFixed(2));
        if (totalCost !== quotation.totalCost) {
          formik.setFieldValue(`quotations.${index}.totalCost`, totalCost);
        }
      });
    }, 300);

    debouncedCalculation();
    return () => {
      debouncedCalculation.cancel();
    };
  }, [formik.values.quotations]);

  const uploadFile = async (file, dispatch) => {
    try {
      const formData = new FormData();
      formData.append('uploads', file);
      const response = await dispatch(commonFileUpload(formData));
      return response.payload?.data?.filename || '';
    } catch (error) {
      console.error('File upload failed:', error);
      return '';
    }
  };

  return (
    <GlobalDialog
      maxWidth={'md'}
      fullWidth={true}
      title={'Add Quotation'}
      open={open}
      setOpen={setOpen}
      closeButton={true}
      closeCondition={handleCancel}
      popupButton={
        <>
          {row ? (
            <UpdateButton
              size="small"
              action={() => setOpen(true)}
              disabled={isEditable || row.is_checked || row.is_viewed}
            >
              Update Quotations
            </UpdateButton>
          ) : (
            <AddButton
              size={'large'}
              action={() => {
                setOpen(true);
              }}
            >
              Add Quotation
            </AddButton>
          )}
        </>
      }
      content={
        <>
          {
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                {loading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(255,255,255,0.6)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      zIndex: 10,
                    }}
                  >
                    <Loader3D />
                  </Box>
                )}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <AddProduct />
                  </Box>
                  <Autocomplete
                    disabled={loading}
                    name="productId"
                    id="productId"
                    options={productData?.data || []}
                    getOptionLabel={(option) => option?.product_name || ''}
                    value={productData?.data?.find((product) => product?.product_id == formik.values.productId) || null}
                    onChange={handleProductChange}
                    renderInput={(params) => (
                      <TextField
                        disabled
                        fullWidth
                        autoComplete="off"
                        {...params}
                        label="Products"
                        error={formik.touched.productId && Boolean(formik.errors.productId)}
                        helperText={formik.touched.productId && formik.errors.productId}
                      />
                    )}
                  />
                </Box>

                {formik.values.quotations.map((quotation, index) => (
                  <Stack
                    borderRadius={2}
                    spacing={1}
                    border={1}
                    p={3}
                    sx={{ backgroundColor: '#e4e4e57d' }}
                    mt={2}
                    key={index}
                  >
                    <Typography variant="h6">Quotation {index + 1}</Typography>

                    <Autocomplete
                      disabled={loading}
                      name={`${quotation}.vendorId`}
                      id={`${quotation}.vendorId`}
                      options={vendorData?.data || []}
                      getOptionLabel={(option) => option?.name || ''}
                      value={vendorData?.data?.find((vendor) => vendor?.vendorID == quotation?.vendorId) || null}
                      onChange={(e, value) =>
                        formik.setFieldValue(`quotations.${index}.vendorId`, value?.vendorID || '')
                      }
                      renderInput={(params) => (
                        <TextField
                          disabled
                          fullWidth
                          autoComplete="off"
                          {...params}
                          label="Vendors"
                          error={
                            formik.touched.quotations?.[index]?.vendorId &&
                            Boolean(formik.errors.quotations?.[index]?.vendorId)
                          }
                          helperText={
                            formik.touched.quotations?.[index]?.vendorId && formik.errors.quotations?.[index]?.vendorId
                          }
                        />
                      )}
                    />
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id={`quotations.${index}.quantity`}
                      type="number"
                      label="Quantity"
                      name={`quotations.${index}.quantity`}
                      value={quotation.quantity}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.quotations?.[index]?.quantity &&
                        Boolean(formik.errors.quotations?.[index]?.quantity)
                      }
                      helperText={
                        formik.touched.quotations?.[index]?.quantity && formik.errors.quotations?.[index]?.quantity
                      }
                      onWheel={(e) => e.target.blur()}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        onWheel: (e) => e.target.blur(),
                      }}
                    />
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id={`quotations.${index}.cost`}
                      type="number"
                      label="Cost per Item"
                      name={`quotations.${index}.cost`}
                      value={quotation?.cost}
                      onChange={formik.handleChange}
                      error={
                        formik.touched?.quotations?.[index]?.cost && Boolean(formik.errors?.quotations?.[index]?.cost)
                      }
                      helperText={
                        formik.touched?.quotations?.[index]?.cost ? formik.errors?.quotations?.[index]?.cost : ''
                      }
                      onWheel={(e) => e.target.blur()}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        onWheel: (e) => e.target.blur(),
                      }}
                    />

                    <TextField
                      autoComplete="off"
                      fullWidth
                      id={`quotations.${index}.gst`}
                      type="number"
                      label="GST Amount"
                      name={`quotations.${index}.gst`}
                      value={quotation.gst}
                      onChange={formik.handleChange}
                      error={formik.touched.quotations?.[index]?.gst && Boolean(formik.errors.quotations?.[index]?.gst)}
                      helperText={formik.touched.quotations?.[index]?.gst && formik.errors.quotations?.[index]?.gst}
                      onWheel={(e) => e.target.blur()}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        onWheel: (e) => e.target.blur(),
                      }}
                    />
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id={`quotations.${index}.courrierCharge`}
                      type="number"
                      label="Courrier Charge"
                      name={`quotations.${index}.courrierCharge`}
                      value={quotation.courrierCharge}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.quotations?.[index]?.courrierCharge &&
                        Boolean(formik.errors.quotations?.[index]?.courrierCharge)
                      }
                      helperText={
                        formik.touched.quotations?.[index]?.courrierCharge &&
                        formik.errors.quotations?.[index]?.courrierCharge
                      }
                      onWheel={(e) => e.target.blur()}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        onWheel: (e) => e.target.blur(),
                      }}
                    />
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id={`quotations.${index}.discountAmount`}
                      type="number"
                      label="Discount Amount"
                      name={`quotations.${index}.discountAmount`}
                      value={quotation.discountAmount}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.quotations?.[index]?.discountAmount &&
                        Boolean(formik.errors.quotations?.[index]?.discountAmount)
                      }
                      helperText={
                        formik.touched.quotations?.[index]?.discountAmount &&
                        formik.errors.quotations?.[index]?.discountAmount
                      }
                      onWheel={(e) => e.target.blur()}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        onWheel: (e) => e.target.blur(),
                      }}
                    />

                    {/* <FormControlLabel
                      control={
                        <Checkbox
                          checked={quotation.manualOverride}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `quotations.${index}.manualOverride`,
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Manual Override"
                    /> */}
                    <TextField
                      disabled
                      autoComplete="off"
                      fullWidth
                      id={`quotations.${index}.totalCost`}
                      type="number"
                      label="Total Cost"
                      name={`quotations.${index}.totalCost`}
                      value={quotation.totalCost}
                      onChange={formik.handleChange}
                      onWheel={(e) => e.target.blur()}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        onWheel: (e) => e.target.blur(),
                      }}
                      error={
                        formik.touched.quotations?.[index]?.totalCost &&
                        Boolean(formik.errors.quotations?.[index]?.totalCost)
                      }
                      helperText={
                        formik.touched.quotations?.[index]?.totalCost && formik.errors.quotations?.[index]?.totalCost
                      }
                    />
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id={`quotations.${index}.deliveryDate`}
                      type="date"
                      label="Delivery Date"
                      name={`quotations.${index}.deliveryDate`}
                      value={quotation.deliveryDate}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.quotations?.[index]?.deliveryDate &&
                        Boolean(formik.errors.quotations?.[index]?.deliveryDate)
                      }
                      helperText={
                        formik.touched.quotations?.[index]?.deliveryDate &&
                        formik.errors.quotations?.[index]?.deliveryDate
                      }
                    />

                    <Box>
                      <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">Payment Type</InputLabel>
                        <Select
                          name={`quotations.${index}.paymentType`}
                          id={`quotations.${index}.paymentType`}
                          value={quotation.paymentType}
                          label="Payment Type"
                          onChange={(e) => formik.setFieldValue(`quotations.${index}.paymentType`, e.target.value)}
                        >
                          {paymentTypes?.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {quotation.paymentType == 2 && (
                      <Stack spacing={1}>
                        <TextField
                          autoComplete="off"
                          fullWidth
                          id={`quotations.${index}.installmentCount`}
                          type="number"
                          label="Installment Count"
                          name={`quotations.${index}.installmentCount`}
                          value={quotation.installmentCount}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.quotations?.[index]?.installmentCount &&
                            Boolean(formik.errors.quotations?.[index]?.installmentCount)
                          }
                          helperText={
                            formik.touched.quotations?.[index]?.installmentCount &&
                            formik.errors.quotations?.[index]?.installmentCount
                          }
                          onWheel={(e) => e.target.blur()}
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            onWheel: (e) => e.target.blur(),
                          }}
                        />

                        {quotation.installmentCount > 0 &&
                          [...Array(Number(quotation.installmentCount))].map((_, installmentIndex) => (
                            <TextField
                              required
                              key={installmentIndex}
                              autoComplete="off"
                              fullWidth
                              id={`quotations.${index}.installmentAmounts.${installmentIndex}`}
                              type="number"
                              label={`Installment Amount ${installmentIndex + 1}`}
                              name={`quotations.${index}.installmentAmounts.${installmentIndex}`}
                              value={formik.values.quotations[index].installmentAmounts[installmentIndex] || ''}
                              onChange={(e) => {
                                const newInstallments = [...formik.values.quotations[index].installmentAmounts];
                                newInstallments[installmentIndex] = Number(e.target.value);
                                formik.setFieldValue(`quotations.${index}.installmentAmounts`, newInstallments);
                              }}
                              onWheel={(e) => e.target.blur()}
                              inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                onWheel: (e) => e.target.blur(),
                              }}
                            />
                          ))}
                      </Stack>
                    )}
                    <Box>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              onChange={(e) =>
                                formik.setFieldValue(`quotations.${index}.isRecommended`, e.target.checked)
                              }
                              name={`isRecommended`}
                              checked={formik.values.quotations[index].isRecommended}
                            />
                          }
                          label="Recommended Quotation"
                        />
                      </FormGroup>
                    </Box>

                    <Box>
                      <Button
                        disabled={loading}
                        variant="outlined"
                        component="label"
                        size="small"
                        sx={{ minWidth: 180 }}
                      >
                        {'Upload File'}
                        <input
                          type="file"
                          hidden
                          accept="image/*,.pdf"
                          onChange={async (e) => {
                            const newFile = e.target.files[0];
                            if (newFile) {
                              const fileKey = await uploadFile(newFile, dispatch);
                              if (fileKey) {
                                formik.setFieldValue(`quotations.${index}.quotationAttachedBackendFileName`, fileKey);
                              }
                            }
                          }}
                        />
                      </Button>

                      {/* 🔴 FORM ERROR DISPLAY */}
                      {formik.touched.quotations?.[index]?.quotationAttachedBackendFileName &&
                        formik.errors.quotations?.[index]?.quotationAttachedBackendFileName && (
                          <Typography sx={{ ml: 1 }} variant="caption" color="error">
                            {formik.errors.quotations[index].quotationAttachedBackendFileName}
                          </Typography>
                        )}

                      {quotation.quotationAttachedBackendFileName && (
                        <>
                          {/* If PDF */}
                          {quotation.quotationAttachedBackendFileName.toLowerCase().endsWith('.pdf') ? (
                            <Box mt={1}>
                              <iframe
                                src={`${uploadedFileUrl}/${quotation.quotationAttachedBackendFileName}`}
                                width="200"
                                height="150"
                                title="Uploaded PDF"
                                style={{ border: '1px solid #ccc', borderRadius: 4 }}
                              ></iframe>
                            </Box>
                          ) : (
                            /* Otherwise render Image */
                            <Box mt={1}>
                              <img
                                src={`${uploadedFileUrl}/${quotation.quotationAttachedBackendFileName}`}
                                alt="Uploaded File"
                                width={200}
                                height={150}
                                style={{ objectFit: 'contain', border: '1px solid #ccc', borderRadius: 4 }}
                              />
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Stack>
                ))}

                <Stack spacing={1} direction={direction} mt={2}>
                  <Button variant="outlined" onClick={handleAddQuotation}>
                    Add Another Quotation
                  </Button>
                  {formik.values.quotations.length > 1 && (
                    <Button variant="outlined" onClick={removeQuotation}>
                      Remove Quotation
                    </Button>
                  )}
                </Stack>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Stack direction={'row'} spacing={1}>
                    <CancelButton action={() => handleCancel()}>Cancel</CancelButton>
                    {row ? (
                      <UpdateButton disabled={loading} type="submit">
                        {loading ? 'Updating...' : 'Update'}
                      </UpdateButton>
                    ) : (
                      <SubmitButton disabled={loading} type="submit">
                        {loading ? 'Submiting...' : 'Submit'}
                      </SubmitButton>
                    )}
                  </Stack>
                </Box>
              </Form>
            </FormikProvider>
          }
        </>
      }
    />
  );
};

export default AddOrUpdateQuotation;
